import { useCallback, useRef, useDebugValue } from "react";

import type { FeatureName } from "@lib/featureFlipper";

import { useBrazeDashboardConfig } from "@services/brazeDashboardConfig";

import { useFeatureOnHookName } from "./constants";
import type { FeatureOnDebugValue } from "./types";

const featureObjectFallback = {};

/**
 * Because this is only used in React Typescript code, we can rely upon TS to ensure we don't attempt to access a non-existing flipper.
 *
 * This provides a wrapper around the `features` field of our dashboard config
 */
export const useFeatureOn = () => {
  const features = useBrazeDashboardConfig("features") || featureObjectFallback;
  // Map from feature name to latest feature value seen by the consumer of this hook
  const featuresUsed = useRef<FeatureOnDebugValue>({
    hookName: useFeatureOnHookName,
    hookValue: new Map(),
  });

  useDebugValue(featuresUsed.current);

  return useCallback(
    (featureName: FeatureName) => {
      const featureValue = !!features[featureName];
      featuresUsed.current.hookValue.set(featureName, featureValue);
      return featureValue;
    },
    [features]
  );
};
